/**
 * Core types for bx-forms
 *
 * These types are framework-agnostic and can be used in any environment
 * (browser, Node.js, React, vanilla JS).
 */

import type { ReactNode } from 'react';

// ============================================================================
// FORM TRACKING & ANALYTICS
// ============================================================================

export interface FormTracking {
  brand?: string;
  formName?: string;
  contentRequest?: string;
  contentCategory?: string;
  eloquaCampaign?: string;
  categoryName?: string;
  productName?: string;
}

export interface FormPayload {
  formData: Record<string, unknown>;
  tracking: FormTracking;
  formTemplate: string;
  formId: string;
  submissionMetadata?: FormSubmissionMetadata;
}

export interface FormSubmissionMetadata {
  submissionId: string;
  firstSubmittedAt: string;
  submissionPhase?: 'initial' | 'followUp' | 'singleStep';
}

export type AnalyticsFormName = 'inline' | 'exit-intent' | 'email-form-cta';

export type FormCloseDetail = 'close-page' | 'close-button' | 'close-outside';

export type ModalFormCloseSource =
  | 'close-button'
  | 'close-outside'
  | 'keyboard';

export type EasModalVariant = 'minimal' | 'minimalNew' | 'twoColumn';

export type ModalDisplayFrequency =
  | 'Every Time'
  | 'Every 7 days'
  | 'Once per user, ever';

export type EasModalTriggerConfig =
  | {
      type: 'custom';
    }
  | {
      type: 'scroll-depth';
      scrollDepth: number;
      trackScrollReached?: boolean;
    }
  | {
      type: 'exit-intent';
      delayMs?: number;
    }
  | {
      type: 'time-on-page';
      delayMs: number;
    };

export type AutomaticModalTriggerConfig = Exclude<
  EasModalTriggerConfig,
  { type: 'custom' }
>;

export const EAS_MODAL_TEMPLATE_LABELS = {
  minimal: 'Modal: Minimal',
  minimalNew: 'Modal: Minimal - New',
  twoColumn: 'Modal: Two Column',
} as const satisfies Record<EasModalVariant, string>;

export interface EasModalSidePanelContent {
  eyebrow?: string;
  title?: string;
  description?: string;
  bulletItems?: string[];
  footer?: string;
  content?: ReactNode;
}

// ============================================================================
// CLIENT DATA & CONTEXT
// ============================================================================

export interface ClientDataContextProps {
  pageType?: string;
  pageCategory?: string;
  pagePlacement?: string;
  pageVariant?: string;
  contextCategory?: string;
  contextProduct?: string;
  productId?: string;
  productUrl?: string;
  isBeta?: boolean;
}

export interface ClientData {
  url: string;
  pageUrl: string;
  pageTitle: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  isBeta: boolean;
  contentfulFormId: string;
  googleId: string;
  lyticsId: string;
  adobeId: string;
  uwId: string;
  pageType: string;
  pageCategory: string;
  pagePlacement: string;
  pageVariant: string;
  contextCategory: string;
  contextProduct: string;
  productId: string;
  productUrl: string;
}

// ============================================================================
// API & GATEWAY
// ============================================================================

export interface SubmitFormResult {
  success: boolean;
  error?: string;
}

export interface GatewaySubmitPayload {
  formId: string;
  formTemplate: string;
  tracking: FormTracking;
  formData: Record<string, unknown>;
  submissionMetadata?: FormSubmissionMetadata;
}

export type OnServerLog = (message: string) => void;
